import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import LookupSelect from '../components/LookupSelect';
import { lookupFacility, lookupReportFacility } from '../lookups';

/**
 * The four reports, as one page with a spec each.
 *
 * They are the same thing four times — pick parameters, ask the endpoint, save a
 * CSV — and the old admin writes it out four times, with four copies of the same
 * Blob-and-anchor download. What differs is the parameters, which is a table.
 *
 * Each names the file after what it contains rather than after a fresh UUID, which
 * is what three of the old pages do: `sales-2026-01-01_2026-02-01.csv` beats
 * `9f3c…-a1.csv` in a downloads folder a week later.
 *
 * The historical and current sales reports are two entries here because they are
 * two endpoints with different parameters — the split at 1 July 2024 is real, and
 * the descriptions say which side each covers.
 */

const today = () => new Date().toISOString().slice(0, 10);

const REPORTS = {
  'sales-new': {
    title: 'Sales report',
    lead: 'Sales from 1 July 2024 onwards.',
    fields: ['from', 'to', 'facility', 'byActivityDate', 'includeCancellations', 'cancellationsAsOwnRows'],
    facilityLookup: lookupFacility,
    facilityHint: 'Name or id. Every facility if left empty.',
    filename: form => `sales-${form.from}_${form.to}.csv`,
    run: (api, form) => api.products.generateSalesReportNew({
      from: form.from,
      to: form.to,
      facility: form.facilityId ? { value: form.facilityId } : undefined,
      byActivityDate: form.byActivityDate,
      includeCancellations: form.includeCancellations,
      cancellationsAsOwnRows: form.cancellationsAsOwnRows
    })
  },

  'sales-old': {
    title: 'Historical sales report',
    lead: 'Sales from before 1 July 2024. It reads the report table, so the facility list here is that table’s, not the live one.',
    fields: ['from', 'to', 'facility', 'byActivityDate', 'includeCancellations'],
    facilityLookup: lookupReportFacility,
    facilityHint: 'Name only — searched in the report table. Every facility if left empty.',
    filename: form => `sales-historical-${form.from}_${form.to}.csv`,
    run: (api, form) => api.products.generateSalesReportOld({
      from: form.from,
      to: form.to,
      facility: form.facilityId ? { value: form.facilityId } : undefined,
      byActivityDate: form.byActivityDate,
      includeCancellations: form.includeCancellations
    })
  },

  settlement: {
    title: 'Settlement report',
    lead: 'Transactions in a date range. The end date is excluded.',
    fields: ['from', 'to'],
    filename: form => `settlement-${form.from}_${form.to}.csv`,
    run: (api, form) => api.transactions.generateTransactionReport({ from: form.from, to: form.to })
  },

  'seasonal-courts': {
    title: 'Seasonal court report',
    lead: 'One calendar month of seasonal court activity.',
    fields: ['year', 'month'],
    filename: form => `seasonal-courts-${form.year}-${String(form.month).padStart(2, '0')}.csv`,
    run: (api, form) => api.seasonalcourts.getReport({
      year: parseInt(form.year, 10),
      month: parseInt(form.month, 10)
    })
  }
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const ReportInternal = ({ api, report }) => {
  const spec = REPORTS[report];
  const now = new Date();

  const [form, setForm] = useState({
    from: '',
    to: today(),
    facilityId: '',
    byActivityDate: false,
    includeCancellations: false,
    cancellationsAsOwnRows: false,
    year: String(now.getFullYear()),
    month: String(now.getMonth() + 1)
  });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const has = field => spec.fields.includes(field);
  const set = (name, value) => setForm(previous => ({ ...previous, [name]: value }));

  const ready = has('from')
    ? !!(form.from && form.to && form.from <= form.to)
    : !!(form.year && form.month);

  const download = async () => {
    if (!ready || busy) return;

    setBusy(true);
    setNote(null);
    try {
      const data = await spec.run(api, form);

      if (data === undefined || data === null || data === '') {
        setNote({ tone: 'error', text: 'The report came back empty — nothing was downloaded.' });
        return;
      }

      const blob = new Blob([data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      // Named after its contents, not after a fresh UUID.
      anchor.download = spec.filename(form);
      anchor.click();
      URL.revokeObjectURL(url);

      setNote({ tone: 'info', text: `Downloaded ${spec.filename(form)}.` });
    } catch (error) {
      // `fetchData` puts the server's own text on `error.payload`; `error.message`
      // is the wrapper. The payload first, because "Server returned error:" alone
      // says nothing about which parameter the server objected to.
      setNote({
        tone: 'error',
        text: `The report failed: ${error?.payload?.translatedMessage || error?.message || 'unknown error'}`
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="next-page">
      <BackToList to="/next/reports">← All reports</BackToList>

      <header className="next-page__header">
        <h1>{spec.title}</h1>
        <p className="next-muted">{spec.lead}</p>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <section className="next-card">
        <div className="next-form__grid">
          {has('from') && (
            <>
              <label className="next-filter">
                <span className="next-filter__label">From</span>
                <input
                  className="next-input"
                  type="date"
                  value={form.from}
                  onChange={event => set('from', event.target.value)}
                />
              </label>
              <label className="next-filter">
                <span className="next-filter__label">To</span>
                <input
                  className="next-input"
                  type="date"
                  value={form.to}
                  onChange={event => set('to', event.target.value)}
                />
                <span className={form.from && form.to && form.from > form.to ? 'next-form__error' : 'next-form__hint'}>
                  {form.from && form.to && form.from > form.to
                    ? 'The end is before the start.'
                    : 'Excluded — the report stops the day before.'}
                </span>
              </label>
            </>
          )}

          {has('facility') && (
            <LookupSelect
              label="Facility"
              value={form.facilityId}
              onChange={value => set('facilityId', value)}
              loadOptions={key => spec.facilityLookup(api, key)}
              placeholder="Search a facility"
              hint={spec.facilityHint}
            />
          )}

          {has('year') && (
            <>
              <label className="next-filter">
                <span className="next-filter__label">Year</span>
                <input
                  className="next-input"
                  type="number"
                  value={form.year}
                  onChange={event => set('year', event.target.value)}
                />
              </label>
              <label className="next-filter">
                <span className="next-filter__label">Month</span>
                <select
                  className="next-input"
                  value={form.month}
                  onChange={event => set('month', event.target.value)}
                >
                  {MONTHS.map((name, index) => (
                    <option key={name} value={index + 1}>{name}</option>
                  ))}
                </select>
              </label>
            </>
          )}

          {has('byActivityDate') && (
            <label className="next-filter next-filter--check">
              <input
                type="checkbox"
                checked={form.byActivityDate}
                onChange={event => set('byActivityDate', event.target.checked)}
              />
              <span>By activity date, not purchase date</span>
            </label>
          )}

          {has('includeCancellations') && (
            <label className="next-filter next-filter--check">
              <input
                type="checkbox"
                checked={form.includeCancellations}
                onChange={event => set('includeCancellations', event.target.checked)}
              />
              <span>Include cancellations</span>
            </label>
          )}

          {has('cancellationsAsOwnRows') && (
            <label className="next-filter next-filter--check">
              <input
                type="checkbox"
                checked={form.cancellationsAsOwnRows}
                disabled={!form.includeCancellations}
                title={form.includeCancellations ? undefined : 'Only applies when cancellations are included'}
                onChange={event => set('cancellationsAsOwnRows', event.target.checked)}
              />
              <span>Cancellations as their own rows</span>
            </label>
          )}
        </div>

        <div className="next-form__actions">
          <button
            type="button"
            className="next-btn next-btn--primary"
            disabled={!ready || busy}
            title={ready ? undefined : 'Fill in the dates first'}
            onClick={download}
          >
            {busy ? 'Building the report…' : 'Download CSV'}
          </button>
          <span className="next-muted">
            {busy
              ? 'This can take a while on a wide date range.'
              : (ready ? `Saved as ${spec.filename(form)}` : 'Pick a date range first.')}
          </span>
        </div>
      </section>
    </div>
  );
};

ReportInternal.propTypes = {
  api: PropTypes.object.isRequired,
  report: PropTypes.oneOf(Object.keys(REPORTS)).isRequired
};

const Report = withApi(ReportInternal);

Report.requiresLogin = true;

export default Report;
